import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, catchError } from 'rxjs';

import { AuthModel, AuthenticationResponseModelData, LinkedUserAuthModel, LinkedUserResponseModel } from '../models/auth.model';
import { UserModelData } from '../models/user.model';
import { ForgotPasswordRequest, ResetPasswordRequest, ResolveTenantRequest, ResetPasswordResponseModelData } from '../models/passwords.model';
import { IUnacceptedSiteModelData, UserSiteModel } from '../models/terms-and-conditions.model';
import { IImpersonatedAuthenticationResponseModelData, IImpersonationResponseModelData } from '../models/impersonation.model';

const API_BASE_URL = environment.apiUrl;

@Injectable({
  providedIn: 'root'
})
export class AuthHttpService {

  constructor(private http: HttpClient) { }

  //public methods
  login(auth: AuthModel): Observable<AuthenticationResponseModelData> {
    let url = API_BASE_URL + "api/TokenAuth/Authenticate";
    url = url.replace(/[?&]$/, "");

    return this.http.post<AuthenticationResponseModelData>(url, auth);
  }

  loginAsTenantUser(auth: LinkedUserAuthModel): Observable<AuthenticationResponseModelData> {
    let url = API_BASE_URL + "api/TokenAuth/AuthenticateLinkedUser";
    url = url.replace(/[?&]$/, "");

    const queryParams = new HttpParams().set('isHubUser', 'true');
      return this.http.post<AuthenticationResponseModelData>(url, auth, { params: queryParams });
  }



  getUser(): Observable<UserModelData> {
    let url = API_BASE_URL + 'api/services/app/Session/GetCurrentUserInformation';
    url = url.replace(/[?&]$/, "");
    return this.http.get<UserModelData>(url).pipe(
      catchError((error: any) => {
        throw new Error(error);
      })
    );
  }

  refreshToken(token: string) {
    let url = API_BASE_URL + 'api/TokenAuth/RefreshToken';
    url = url.replace(/[?&]$/, "");

    return this.http.post(url, token);
  }

  getLinkedUsers(parentId: number, isHubUser: boolean | null = null): Observable<LinkedUserResponseModel[]> {
    let url = API_BASE_URL + 'api/User/GetLinkedUsers?parentId='+parentId;

    let params = new HttpParams();
    if (isHubUser !== null) {
      params = params.set('isHubUser', isHubUser.toString());
    }

    return this.http.get<LinkedUserResponseModel[]>(url, { params: params });
  }

  resolveAndUpdateTenantId(request: ResolveTenantRequest): Observable<any> {
    let url = API_BASE_URL + "api/services/app/Account/ResolveTenantId";
    url = url.replace(/[?&]$/, "");

    return this.http.post<any>(url, request);
  }

  requestPasswordResetCode(request: ForgotPasswordRequest): Observable<void> {
    let url = API_BASE_URL + "api/services/app/Account/SendPasswordResetCode";
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, request);
  }

  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponseModelData> {
    let url = API_BASE_URL + "api/services/app/Account/ResetPassword";
    url = url.replace(/[?&]$/, "");

    return this.http.post<ResetPasswordResponseModelData>(url, request);
  }

  addUnacceptedSites(sites: UserSiteModel[]): Observable<void> {
    let url = API_BASE_URL + 'api/services/app/UserSiteAcceptance/Create';
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, sites);
  }

  getUnacceptedSites(): Observable<IUnacceptedSiteModelData> {
    let url = API_BASE_URL + "api/services/app/UserSiteAcceptance/GetUnacceptedSites";
    url = url.replace(/[?&]$/, "");
    return this.http.get<IUnacceptedSiteModelData>(url);
  }

  setAcceptanceDate(siteIds: number[]): Observable<void> {
    let url = API_BASE_URL + 'api/services/app/UserSiteAcceptance/UpdateAcceptanceDate';
    url = url.replace(/[?&]$/, "");

    return this.http.put<void>(url, siteIds);
  }

  revokeAcceptedSites(sites: UserSiteModel[]): Observable<void> {
    let url = API_BASE_URL + 'api/services/app/UserSiteAcceptance/RevokeAcceptance';
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, sites);
  }

  impersonatedLogin(token: string): Observable<IImpersonatedAuthenticationResponseModelData> {
    let url = API_BASE_URL + 'api/TokenAuth/ImpersonatedAuthenticate?impersonationToken=' + encodeURIComponent(token);
    url = url.replace(/[?&]$/, "");

    return this.http.post<IImpersonatedAuthenticationResponseModelData>(url, null);
  }

  unimpersonate(): Observable<IImpersonationResponseModelData> {
    let url = API_BASE_URL + 'api/services/app/Account/BackToImpersonator';
    url = url.replace(/[?&]$/, "");

    return this.http.post<IImpersonationResponseModelData>(url, null);
  }

  updateLastLoginTime(userId: number): Observable<boolean> {
    let url = API_BASE_URL + "api/User/UpdateLastLoginTime";
    url = url.replace(/[?&]$/, "");

    return this.http.put<boolean>(url, userId);
  }

  logout(): void {
    const url = API_BASE_URL + 'logout';
    this.http.post(url, {}).subscribe();
  }
}
